import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Popover, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import { format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { uk } from 'date-fns/locale';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export type DateRangeView = 'day' | 'week' | 'month' | '2weeks';

interface DateRangeNavigatorProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  view: DateRangeView;
  onViewChange: (view: DateRangeView) => void;
  disableViewChange?: boolean;
}

export const DateRangeNavigator: React.FC<DateRangeNavigatorProps> = ({
  currentDate,
  onDateChange,
  view,
  onViewChange,
  disableViewChange = false,
}) => {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

  const handlePrev = useCallback(() => {
    switch (view) {
      case 'month': onDateChange(subMonths(currentDate, 1)); break;
      case 'week':  onDateChange(subWeeks(currentDate, 1));  break;
      case '2weeks': onDateChange(subWeeks(currentDate, 2)); break;
      case 'day':   onDateChange(subDays(currentDate, 1));   break;
    }
  }, [view, currentDate, onDateChange]);

  const handleNext = useCallback(() => {
    switch (view) {
      case 'month': onDateChange(addMonths(currentDate, 1)); break;
      case 'week':  onDateChange(addWeeks(currentDate, 1));  break;
      case '2weeks': onDateChange(addWeeks(currentDate, 2)); break;
      case 'day':   onDateChange(addDays(currentDate, 1));   break;
    }
  }, [view, currentDate, onDateChange]);



  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if ((document.activeElement as HTMLElement)?.isContentEditable) return;
      if (e.key === 'ArrowLeft') handlePrev();
      else handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext]);

  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: DateRangeView | null,
  ) => {
    if (newView !== null) {
      onViewChange(newView);
    }
  };

  const renderDateLabel = () => {
    switch (view) {
      case 'month':
        return format(currentDate, 'MM.yyyy');
      case 'week': {
        const start = startOfWeek(currentDate, { weekStartsOn: 1 });
        const end = endOfWeek(currentDate, { weekStartsOn: 1 });
        if (start.getMonth() === end.getMonth()) {
          return `${format(start, 'd')}–${format(end, 'd.MM.yy')}`;
        }
        return `${format(start, 'd.MM')}–${format(end, 'd.MM.yy')}`;
      }
      case '2weeks': {
        const start = startOfWeek(currentDate, { weekStartsOn: 1 });
        const end = addDays(start, 13);
        if (start.getMonth() === end.getMonth()) {
          return `${format(start, 'd')}–${format(end, 'd.MM.yy')}`;
        }
        return `${format(start, 'd.MM')}–${format(end, 'd.MM.yy')}`;
      }
      case 'day':
        return format(currentDate, 'd.MM.yy, EEE', { locale: uk });
      default:
        return '';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'nowrap',
      }}
    >
      {!disableViewChange && (
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{ height: '32px' }}
        >
          <ToggleButton value="day">1d</ToggleButton>
          <ToggleButton value="week">1w</ToggleButton>
          <ToggleButton value="2weeks">2w</ToggleButton>
          <ToggleButton value="month">M</ToggleButton>
        </ToggleButtonGroup>
      )}

      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          minWidth: view === 'day' ? 120 : 90,
          textAlign: 'center',
          textTransform: 'capitalize',
          whiteSpace: 'nowrap',
        }}
      >
        {renderDateLabel()}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <IconButton size="small" onClick={handlePrev} sx={{ borderRadius: '4px 0 0 4px' }}>
          <ChevronLeft />
        </IconButton>
        <IconButton size="small" onClick={e => setPickerAnchor(e.currentTarget)} sx={{ borderRadius: 0, borderLeft: '1px solid', borderRight: '1px solid', borderColor: 'divider' }}>
          <Today sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={handleNext} sx={{ borderRadius: '0 4px 4px 0' }}>
          <ChevronRight />
        </IconButton>
      </Box>

      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
          <DateCalendar
            value={currentDate}
            onChange={date => {
              if (date) {
                onDateChange(date);
                setPickerAnchor(null);
              }
            }}
          />
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};
